import ImageUploadPreview from '../ui/ImageUpload/ImageUploadPreview';
const formatDate = (value) => value ? new Date(value).toISOString().slice(0, 10) : '';

import {
  Box, Button, Drawer, TextField, Typography, FormControlLabel, Checkbox,
  FormControl, InputLabel, Select, MenuItem, CircularProgress
} from '@mui/material';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useInitialFormState } from '../../hooks/useInitialFormState';
import { uploadImage } from '../../api/hotelinfoApi';
import { deleteRemovedImages } from '../../utils/imageUtils';



export default function EventFormModal({ open, onClose, onSubmit, initialData }) {
  const memoizedInitialData = useMemo(() => {
    return open && initialData && initialData._id ? initialData : null;
  }, [open, initialData]);
  const [form, setForm] = useState({});
  const [uploadingImages, setUploadingImages] = useState(false);
  useInitialFormState(memoizedInitialData, setForm, ['imageUrls', 'availableHours', 'tags']);

  useEffect(() => {
    if (!initialData._id) {
      setForm({});
    }
  }, [initialData]);

  const handleImageUrlsChange = useCallback(
    (updatedUrls) => {
      setForm(prev => ({ ...prev, imageUrls: updatedUrls }));
    },
    [setForm]
  );

  const handleSubmit = () => {
    onSubmit(form);
    setTimeout(() => {
      deleteRemovedImages(initialData?.imageUrls || [], form.imageUrls || []);
    }, 500);
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 600, p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {form._id ? 'Edit' : 'Add'} Event
        </Typography>

        <TextField fullWidth label="Event Title" margin="normal" value={form.title || ''}
          onChange={e => setForm({ ...form, title: e.target.value })} />

        <TextField fullWidth label="Description" margin="normal" multiline rows={3} value={form.description || ''}
          onChange={e => setForm({ ...form, description: e.target.value })} />

        <TextField fullWidth type="number" label="Price" margin="normal" value={form.price ?? ''}
          onChange={e => setForm({ ...form, price: Number(e.target.value) })} />

        <TextField fullWidth label="Tags (comma separated)" margin="normal" value={Array.isArray(form.tags) ? form.tags.join(', ') : ''}
          onChange={e => setForm({ ...form, tags: e.target.value.split(',').map(t => t.trim()) })} />

        <FormControl fullWidth margin="normal">
          <InputLabel>Status</InputLabel>
          <Select value={form.status || 'active'} onChange={e => setForm({ ...form, status: e.target.value })}>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>

        <FormControlLabel control={
          <Checkbox checked={form.isFeatured || false}
            onChange={e => setForm({ ...form, isFeatured: e.target.checked })} />
        } label="Featured Event" />

        <TextField fullWidth type="number" label="Priority" margin="normal" value={form.priority ?? ''}
          onChange={e => setForm({ ...form, priority: Number(e.target.value) })} />

        <TextField fullWidth type="date" label="Start Date" margin="normal" InputLabelProps={{ shrink: true }}
          value={formatDate(form.startDate)} onChange={e => setForm({ ...form, startDate: e.target.value })} />

        <TextField fullWidth type="date" label="End Date" margin="normal" InputLabelProps={{ shrink: true }}
          value={formatDate(form.endDate)} onChange={e => setForm({ ...form, endDate: e.target.value })} />

        <TextField fullWidth type="time" label="Start Time" margin="normal" value={form.availableHours?.from || ''}
          onChange={e => setForm({ ...form, availableHours: { ...form.availableHours, from: e.target.value } })} />

        <TextField fullWidth type="time" label="Finish Time" margin="normal" value={form.availableHours?.to || ''}
          onChange={e => setForm({ ...form, availableHours: { ...form.availableHours, to: e.target.value } })} />

        <FormControlLabel control={
          <Checkbox checked={form.bookingRequired || false}
            onChange={e => setForm({ ...form, bookingRequired: e.target.checked })} />
        } label="Booking Required" />

        <TextField fullWidth label="Location" margin="normal" value={form.location || ''}
          onChange={e => setForm({ ...form, location: e.target.value })} />

        <TextField fullWidth label="Speaker/Host" margin="normal" value={form.speakerOrHost || ''}
          onChange={e => setForm({ ...form, speakerOrHost: e.target.value })} />
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Upload Images
          </Typography>
          <ImageUploadPreview
            initialUrls={form.imageUrls || []}
            onChange={handleImageUrlsChange}
            uploadImageFn={async (file, onProgress) => {
              setUploadingImages(true);
              const url = await uploadImage(file, onProgress);
              setUploadingImages(false);
              return url;
            }}
          />
        </Box>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{ ml: 1 }}
            disabled={uploadingImages}
            startIcon={uploadingImages ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {form._id ? 'Update' : 'Add'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}